<template>
  <div>

    <!-- Searching-for-existing-patient indicator — shown while the background lookup
         triggered by Enter is in flight, so the user doesn't assume nothing happened
         and close the modal or start filling the form in manually. -->
    <transition name="fade">
      <div
        v-if="isSearchingPatient"
        class="mb-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800"
      >
        <svg class="animate-spin h-5 w-5 text-blue-600 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span class="text-sm font-medium">Checking for an existing patient record&hellip; please wait</span>
      </div>
    </transition>

  <div class="grid grid-cols-2 gap-4">

    <!-- ID Number - OPTIONAL -->
    <div>
      <label for="input1" class="block text-sm font-medium text-gray-700">
        ID Number/ Passport Number
      </label>
      <input
        id="input1"
        type="text"
        v-model="patientRegForm.patient.idNumber"
        @input="updateForm"
        @keyup.enter="searchPatient"
        @keyup="validateIdNumber"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter ID Number"
      />
      <p v-if="idError" class="text-red-500 text-sm mt-1">{{ idError }}</p>

      <input type="hidden" v-model="patientRegForm.patient.patientId" />
    </div>

    <!-- Phone Number - OPTIONAL -->
    <div>
      <label for="input2" class="block text-sm font-medium text-gray-700">
        Phone Number
      </label>
      <input
        id="input2"
        type="text"
        v-model="patientRegForm.patient.phoneNumber"
        @input="updateForm"
        @keyup.enter="searchPatient"
        @keyup="validatePhone"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="E.g 0711223344"
      />
      <p v-if="phoneError" class="text-red-500 text-sm mt-1">
        {{ phoneError }}
      </p>
    </div>

    <!-- Title - OPTIONAL -->
    <div>
      <label for="input3" class="block text-sm font-medium text-gray-700">
        Title
      </label>

      <select
        v-model="patientRegForm.patient.title"
        @input="updateForm"
        id="patient-title"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
      >
        <option disabled value="">Please select a title</option>
        <option v-for="title in titles" :key="title" :value="title">
          {{ title }}
        </option>
      </select>
    </div>

    <!-- Full Name - MANDATORY -->
    <div>
      <label for="input4" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        Full Name
      </label>
      <input
        id="input4"
        type="text"
        v-model="patientRegForm.patient.fullName"
        @input="updateForm"
        @keyup.enter="searchPatient"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter full name"
        required
      />
    </div>

    <!-- D.O.B - MANDATORY -->
    <div>
      <label for="input-dob" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        D.O.B
      </label>
      <input
        id="input-dob"
        type="date"
        v-model="patientRegForm.patient.dob"
        @input="updateForm"
        @change="calculateAgeFromDob"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Select date of birth"
        required
      />
    </div>

    <!-- Age - MANDATORY -->
    <div>
      <label for="input-age" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        Age {{ patientRegForm.patient.ageType == 1 ? "(Years)" : "(Months)" }}
      </label>
      <input
        id="input-age"
        type="text"
        v-model="patientRegForm.patient.age"
        @input="updateForm"
        @change="calculateDobFromAge"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter age"
        required
      />
    </div>

    <!-- Gender - MANDATORY -->
    <div>
      <label for="input-gender" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        Gender
      </label>

      <select
        v-model="patientRegForm.patient.gender"
        @input="updateForm"
        id="input-gender"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        required
      >
        <option disabled value="">Please select gender</option>
        <option v-for="gender in genders" :key="gender" :value="gender">
          {{ gender }}
        </option>
      </select>
    </div>

    <!-- Relationship - MANDATORY -->
    <div>
      <label for="input-relationship" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        Relationship
      </label>

      <select
        v-model="patientRegForm.patient.relationship"
        @input="updateForm"
        id="input-relationship"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        required
      >
        <option disabled value="">Select relationship</option>
        <option
          v-for="relationship in relationships"
          :key="relationship"
          :value="relationship"
        >
          {{ relationship }}
        </option>
      </select>
    </div>

    <!-- Visit Type - MANDATORY -->
    <div>
      <label for="input-visit-type" class="block text-sm font-medium text-gray-700">
        <span class="text-red-500">*</span>
        Visit Type
      </label>

      <select
        v-model="patientRegForm.patient.visitType"
        @input="updateForm"
        id="input-visit-type"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        required
      >
        <option disabled value="">Select visit type</option>
        <option
          v-for="visitType in visitTypes"
          :key="visitType"
          :value="visitType"
        >
          {{ visitType }}
        </option>
      </select>
    </div>

    <!-- Blood Group - OPTIONAL -->
    <div>
      <label for="input-blood-group" class="block text-sm font-medium text-gray-700">
        Blood Group
      </label>

      <select
        v-model="patientRegForm.patient.bloodGroup"
        @input="updateForm"
        id="input-blood-group"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
      >
        <option disabled value="">Select blood group</option>
        <option
          v-for="bloodGroup in bloodGroups"
          :key="bloodGroup"
          :value="bloodGroup"
        >
          {{ bloodGroup }}
        </option>
      </select>
    </div>

    <!-- E-mail Address - OPTIONAL -->
    <div>
      <label for="input-email" class="block text-sm font-medium text-gray-700">
        E-mail Address
      </label>
      <input
        id="input-email"
        type="text"
        v-model="patientRegForm.patient.emailAddress"
        @input="updateForm"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter email address"
      />
    </div>

    <!-- Occupation - OPTIONAL -->
    <div>
      <label for="input-occupation" class="block text-sm font-medium text-gray-700">
        Occupation
      </label>
      <input
        id="input-occupation"
        type="text"
        v-model="patientRegForm.patient.occupation"
        @input="updateForm"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter Occupation"
      />
    </div>

    <!-- Marital Status - OPTIONAL -->
    <div>
      <label for="input-marital-status" class="block text-sm font-medium text-gray-700">
        Marital Status
      </label>
      <select
        v-model="patientRegForm.patient.maritalStatus"
        @input="updateForm"
        id="input-marital-status"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
      >
        <option disabled value="">Select marital status</option>
        <option
          v-for="maritalStatus in maritalStatuses"
          :key="maritalStatus"
          :value="maritalStatus"
        >
          {{ maritalStatus }}
        </option>
      </select>
    </div>

    <!-- Residence - OPTIONAL -->
    <div>
      <label for="input-residence" class="block text-sm font-medium text-gray-700">
        Residence
      </label>
      <input
        id="input-residence"
        type="text"
        v-model="patientRegForm.patient.residence"
        @input="updateForm"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter residence"
      />
    </div>

    <!-- County - OPTIONAL -->
    <div>
      <label for="input-county" class="block text-sm font-medium text-gray-700">
        County
      </label>
      <select
        v-model="patientRegForm.patient.county"
        @input="updateForm"
        id="input-county"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-sm focus:ring-blue-500 focus:border-blue-500"
      >
        <option disabled value="">Select County</option>
        <option v-for="county in counties" :key="county" :value="county">
          {{ county }}
        </option>
      </select>
    </div>

    <!-- Nationality - OPTIONAL -->
    <div>
      <label for="input-nationality" class="block text-sm font-medium text-gray-700">
        Nationality
      </label>
      <select
        v-model="patientRegForm.patient.nationality"
        @input="updateForm"
        id="input-nationality"
        class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-sm focus:ring-blue-500 focus:border-blue-500"
      >
        <option disabled value="">Select Nationality</option>
        <option
          v-for="nationality in nationalities"
          :key="nationality"
          :value="nationality"
        >
          {{ nationality }}
        </option>
      </select>
    </div>
  </div>
  </div>
</template>

<script>
import countiesData from "@/assets/test_data/counties.json";
import { validatePhoneNumber } from "@/services/customUtilities";

export default {
  name: "PatientInformationForm",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    relationships: {
      type: Array,
      required: true,
    },
    isSearchingPatient: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      patientRegForm: { ...this.formData },
      titles: ["Mr.", "Mrs.", "Miss", "Dr.", "Prof.", "Mx.", "Other"],
      genders: ["Male", "Female", "Other"],
      maritalStatuses: [
        "Single",
        "Married",
        "Divorced",
        "Widowed",
        "Separated",
        "Other",
      ],
      visitTypes: ["Consultation", "Procedure/Services", "Registration Only"],
      bloodGroups: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Other"],
      nationalities: ["Kenyan", "Foreigner"],
      paymentProviders: ["cash", "mpesa", "airtelmoney"],
      counties: countiesData.counties,
      calculatedAge: null, // Calculated age based on dob
      calculatedDob: "", // Calculated dob based on age
      phoneError: "",
      idError: "",
    };
  },
  methods: {
    updateForm() {
      this.$emit("update:formData", this.patientRegForm);
      console.log(this.patientRegForm); // Emit updated form data to parent
    },
    calculateAgeFromDob() {
      const birthDate = new Date(this.patientRegForm.patient.dob);
      const today = new Date();
      let monthDiff =
        (today.getFullYear() - birthDate.getFullYear()) * 12 +
        (today.getMonth() - birthDate.getMonth());

      // Adjust for day of month
      if (today.getDate() < birthDate.getDate()) {
        monthDiff--;
      }

      if (monthDiff < 12) {
        this.calculatedAge = `${monthDiff} months`;
        this.patientRegForm.patient.ageType = 2;
        this.patientRegForm.patient.age = monthDiff;
      } else {
        const years = Math.floor(monthDiff / 12);
        this.patientRegForm.patient.ageType = 1;
        this.calculatedAge = years;
        this.patientRegForm.patient.age = years;
      }
    },

    // Method to calculate DOB from age
    calculateDobFromAge() {
      if (
        this.patientRegForm.patient.age !== null &&
        this.patientRegForm.patient.age >= 0
      ) {
        const today = new Date();
        const birthYear = today.getFullYear() - this.patientRegForm.patient.age;
        const birthDate = new Date(
          birthYear,
          today.getMonth(),
          today.getDate()
        );

        // Set the calculated date of birth in the format YYYY-MM-DD
        this.calculatedDob = birthDate.toISOString().split("T")[0];

        this.patientRegForm.patient.dob = this.calculatedDob;
      }
    },
    searchPatient() {
      this.$emit("searchPatient");
    },
    validatePhone(event) {
      // Remove non-numeric characters
      const numericValue = event.target.value.replace(/\D/g, "");

      this.patientRegForm.patient.phoneNumber = numericValue;

      // Only validate if phone number is provided
      if (!numericValue || numericValue.trim() === "") {
        this.phoneError = null;
        return;
      }

      const result = validatePhoneNumber(
        this.patientRegForm.patient.phoneNumber
      );
      console.log("phone no validation", result);
      if (!result.valid) {
        this.phoneError = result.message;
      } else {
        this.phoneError = null;
      }
    },
    validateIdNumber(event) {
      // Only validate if ID number is provided
      if (!event.target.value || event.target.value.trim() === "") {
        this.idError = "";
        return;
      }
    },
  },
};
</script>

<style scoped>
/* Add any additional styles if needed */

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>